import { apiService } from './api';

export interface UnitConversion {
  unitConversionId: number;
  fromUnitId: number;
  fromUnitName: string;
  toUnitId: number;
  toUnitName: string;
  conversionFactor: number;
  formula?: string;
  isProductSpecific: boolean;
  productId?: number;
  productName?: string;
  precisionScale?: number;
  roundingMethod?: string;
  effectiveFromDate?: string;
  effectiveToDate?: string;
  isActive: boolean;
  remarks?: string;
  createdDate: string;
}

export interface CreateUnitConversionDTO {
  fromUnitId: number;
  toUnitId: number;
  conversionFactor: number;
  formula?: string;
  isProductSpecific: boolean;
  productId?: number;
  precisionScale?: number;
  roundingMethod?: string;
  effectiveFromDate?: string;
  effectiveToDate?: string;
  remarks?: string;
}

export interface UpdateUnitConversionDTO {
  fromUnitId: number;
  toUnitId: number;
  conversionFactor: number;
  formula?: string;
  isProductSpecific: boolean;
  productId?: number;
  precisionScale?: number;
  roundingMethod?: string;
  effectiveFromDate?: string;
  effectiveToDate?: string;
  isActive: boolean;
  remarks?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
  };
}

export interface ConvertQuantityRequest {
  quantity: number;
  fromUnitId: number;
  toUnitId: number;
  productId?: number;
}

class UnitConversionService {
  async getUnitConversions(pageNumber: number = 1, pageSize: number = 50) {
    try {
      const response = await apiService.get<PaginatedResponse<UnitConversion>>(
        '/unit-conversions',
        { pageNumber, pageSize }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching unit conversions:', error);
      throw error;
    }
  }

  async getUnitConversionById(id: number) {
    try {
      const response = await apiService.get<UnitConversion>(`/unit-conversions/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching unit conversion:', error);
      throw error;
    }
  }

  async getConversionsByFromUnit(fromUnitId: number) {
    try {
      const response = await apiService.get<UnitConversion[]>(`/unit-conversions/from/${fromUnitId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching conversions by from unit:', error);
      throw error;
    }
  }

  async getConversionsByToUnit(toUnitId: number) {
    try {
      const response = await apiService.get<UnitConversion[]>(`/unit-conversions/to/${toUnitId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching conversions by to unit:', error);
      throw error;
    }
  }

  async getConversion(fromUnitId: number, toUnitId: number) {
    try {
      const response = await apiService.get<UnitConversion>(`/unit-conversions/${fromUnitId}/${toUnitId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching conversion:', error);
      throw error;
    }
  }

  async getProductSpecificConversions(productId: number) {
    try {
      const response = await apiService.get<UnitConversion[]>(`/unit-conversions/product/${productId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching product-specific conversions:', error);
      throw error;
    }
  }

  async createUnitConversion(dto: CreateUnitConversionDTO) {
    try {
      const response = await apiService.post<UnitConversion>('/unit-conversions', dto);
      return response.data.data;
    } catch (error) {
      console.error('Error creating unit conversion:', error);
      throw error;
    }
  }

  async updateUnitConversion(id: number, dto: UpdateUnitConversionDTO) {
    try {
      const response = await apiService.put<UnitConversion>(`/unit-conversions/${id}`, dto);
      return response.data.data;
    } catch (error) {
      console.error('Error updating unit conversion:', error);
      throw error;
    }
  }

  async deleteUnitConversion(id: number) {
    try {
      await apiService.delete(`/unit-conversions/${id}`);
    } catch (error) {
      console.error('Error deleting unit conversion:', error);
      throw error;
    }
  }

  async convertQuantity(request: ConvertQuantityRequest) {
    try {
      const response = await apiService.post<number>('/unit-conversions/convert', request);
      return response.data.data;
    } catch (error) {
      console.error('Error converting quantity:', error);
      throw error;
    }
  }
}

const unitConversionService = new UnitConversionService();
export default unitConversionService;
